<template>
	<div class="auth">
		<div class="auth-card">
			<div class="header-row">
				<img src="/logo-m.svg" alt="Logo" class="logo-img" />
				<h2 class="greeting">{{ timeBasedGreeting }}</h2>
			</div>
			
			<form v-if="authMode === 'login'" @submit.prevent="handleSubmit" class="auth-form">
				<div class="form-group">
					<input
						type="email"
						id="email"
						v-model="email"
						name="email"
						class="email-input"
						placeholder="Enter your email"
						:class="{ 'error': submitted && emailError }"
						autocomplete="email"
						required
					/>
					<p v-if="submitted && emailError" class="error-message">{{ emailError }}</p>
				</div>

				<div class="form-group">
					<div class="password-input-wrapper">
						<input
							ref="passwordInput"
							:type="showPassword ? 'text' : 'password'"
							id="password"
							v-model="password"
							name="password"
							class="password-input"
							placeholder="Enter your password"
							:class="{ 'error': (submitted && passwordError) || error }"
							autocomplete="current-password"
							required
						/>
						<button
							type="button"
							class="toggle-password"
							@click="togglePassword"
							:aria-label="showPassword ? 'Hide password' : 'Show password'"
						>
							<svg v-if="!showPassword" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
								<path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
								<circle cx="12" cy="12" r="3"/>
							</svg>
							<svg v-else width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
								<path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
								<line x1="1" y1="1" x2="23" y2="23"/>
							</svg>
						</button>
					</div>
					<p v-if="submitted && passwordError" class="error-message">{{ passwordError }}</p>
					<p v-if="error" class="error-message">{{ error }}</p>
				</div>

				<div class="form-group form-options">
					<div class="remember-me">
						<input type="checkbox" id="remember-me" v-model="rememberMe" />
						<label for="remember-me">Remember me</label>
					</div>
					<a href="#" @click.prevent="handleForgotPassword" class="forgot-password-link">Forgot Password?</a>
				</div>
				
				<button
					type="submit"
					ref="submitButton"
					class="submit-button"
					:disabled="isLoading"
				>
					<span v-if="isLoading">Signing in...</span>
					<span v-else>Sign In</span>
				</button>
			</form>
		</div>
	</div>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useAuthStore } from '../stores/auth';

const authMode = ref('login');
const email = ref('');
const password = ref('');
const rememberMe = ref(true);
const showPassword = ref(false);
const submitted = ref(false);
const isLoading = ref(false);
const error = ref(null);
const currentTime = ref(new Date());
const passwordInput = ref(null);

const router = useRouter();
const route = useRoute();
const auth = useAuthStore();

const emailError = computed(() => {
	if (!email.value) return 'Email is required';
	const emailRegex = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
	if (!emailRegex.test(email.value)) return 'Invalid email format';
	return null;
});

const passwordError = computed(() => {
	if (!password.value) return 'Password is required';
	if (password.value.length < 8) return 'Password must be at least 8 characters';
	return null;
});

const timeBasedGreeting = computed(() => {
	const hour = currentTime.value.getHours();
	if (hour >= 5 && hour < 12) {
		return 'Good morning';
	} else if (hour >= 12 && hour < 17) {
		return 'Good afternoon';
	} else if (hour >= 17 && hour < 22) {
		return 'Good evening';
	} else {
		return 'Good night';
	}
});

const togglePassword = () => {
	showPassword.value = !showPassword.value;
};

const handleForgotPassword = () => {
	window.alert('Please contact administrator to reset your password.');
};

const handleSubmit = async () => {
	submitted.value = true;
	error.value = null;
	
	if (emailError.value || passwordError.value) {
		return;
	}
	
	try {
		isLoading.value = true;
		
		const { success, error: loginError } = await auth.login({
			email: email.value,
			password: password.value,
			remember: rememberMe.value
		});
		
		if (success) {
			const redirectTo =
				typeof route.query.redirect === 'string' ? route.query.redirect : null;
			const resolvedRedirect = redirectTo ? router.resolve(redirectTo) : null;
			const hasMatch = resolvedRedirect?.matched?.length;
			router.push(hasMatch ? resolvedRedirect : { name: 'dashboard' });
		} else {
			error.value = loginError;
		}
	} catch (e) {
		error.value = e.message || 'Authentication failed';
		console.error('handleSubmit', e);
	} finally {
		isLoading.value = false;
	}
};

onMounted(() => {
	currentTime.value = new Date();
	if (authMode.value === 'login' && passwordInput.value) {
		passwordInput.value.focus();
	}
});
</script>

<style lang="sass" scoped>
@use '@/assets/styles/variables.sass' as variables

.auth
	display: flex
	justify-content: center
	align-items: center
	min-height: 100vh
	padding: variables.$spacing-large
	background-color: var(--noc-background-secondary)

.auth-card
	width: 100%
	max-width: variables.$auth-card-max-width
	background-color: var(--noc-card-background, var(--noc-background))
	border-radius: variables.$border-radius
	padding: variables.$spacing-xxlarge variables.$spacing-xxlarge
	text-align: center
	border: 1px solid var(--border-color)
	box-shadow: variables.$shadow-soft

.header-row
	display: flex
	flex-direction: row-reverse
	justify-content: space-between
	align-items: flex-end
	width: 100%
	margin-bottom: variables.$spacing-xlarge

.logo-img
	width: 40px
	height: auto
	object-fit: contain
	margin: 0
	transform: translateY(10px)

.greeting
	color: var(--text-color)
	font-size: 1.2em
	font-weight: variables.$font-weight-semibold
	font-family: variables.$font-family-base
	margin-top: 0
	margin-bottom: 0
	text-align: right
	line-height: 1
	
.auth-form
	width: 100%
	display: flex
	flex-direction: column
	gap: variables.$spacing-large

.form-group
	margin-bottom: 0
	text-align: left
	display: flex
	flex-direction: column
	gap: variables.$spacing-xsmall
	
	label
		color: var(--text-color-secondary)
		font-weight: variables.$font-weight-medium
		font-size: 0.8rem
		font-family: variables.$font-family-base

.email-input,
.password-input-wrapper .password-input
	width: 100%
	padding: variables.$spacing-medium variables.$spacing-large
	border: 1px solid var(--border-color)
	border-radius: variables.$border-radius
	font-size: 0.9rem
	font-family: variables.$font-family-base
	color: var(--text-color)
	background-color: var(--noc-input-background, var(--noc-background-secondary))
	transition: border-color 0.2s ease, box-shadow 0.2s ease
	
	&:focus
		background-color: var(--noc-input-background-focus, var(--noc-background-secondary))
	
	&::placeholder
		color: var(--text-color-placeholder, var(--text-color-secondary))
		font-family: variables.$font-family-base
		opacity: 0.7

.password-input-wrapper
	position: relative
	display: flex
	
	.password-input
		padding-right: 50px
	
	.toggle-password
		position: absolute
		right: 1px
		top: 1px
		bottom: 1px
		width: variables.$password-toggle-width
		display: flex
		align-items: center
		justify-content: center
		background: transparent
		border: none
		color: var(--text-color-secondary)
		font-size: variables.$font-size-xsmall
		cursor: pointer
		padding: 0
		border-radius: 0 (variables.$border-radius - 1px) (variables.$border-radius - 1px) 0
		transition: color 0.2s ease, background-color 0.2s ease
		
		&:hover
			color: var(--noc-primary-hover, var(--noc-blue))

.error-message
	font-size: 0.8rem
	margin-top: variables.$spacing-xsmall
	font-family: variables.$font-family-base
	color: var(--noc-danger-text, red)

.reset-info
	margin-bottom: variables.$spacing-medium
	color: var(--text-color-secondary)
	font-size: variables.$font-size-small
	line-height: variables.$line-height-loose

.form-options
	display: flex
	flex-direction: row
	justify-content: space-between
	align-items: center
	font-size: 0.8rem
	font-family: variables.$font-family-base
	gap: variables.$spacing-medium

.remember-me
	display: flex
	align-items: center
	gap: variables.$spacing-xsmall
	
	input[type="checkbox"]
		accent-color: var(--noc-primary, var(--noc-blue))
		cursor: pointer
		width: variables.$checkbox-size
		height: variables.$checkbox-size
	
	label
		color: var(--text-color-secondary)
		cursor: pointer
		margin-bottom: 0
		font-weight: variables.$font-weight-normal
		font-size: 0.8rem

.form-actions
	display: flex
	justify-content: center
	margin-top: variables.$spacing-small

.forgot-password-link
	color: var(--noc-primary, var(--noc-blue))
	text-decoration: none
	font-size: 0.8rem
	font-family: variables.$font-family-base
	transition: color 0.2s ease
	
	&:hover
		text-decoration: underline
		color: var(--noc-primary-hover, var(--noc-primary, var(--noc-blue)))

.submit-button
	width: 100%
	padding: variables.$spacing-medium variables.$spacing-large
	background-color: var(--noc-primary, var(--noc-blue))
	color: var(--noc-primary-contrast-text, var(--noc-background))
	border: none
	border-radius: variables.$border-radius
	font-size: 0.9rem
	font-weight: variables.$font-weight-semibold
	font-family: variables.$font-family-base
	cursor: pointer
	transition: background-color 0.2s ease, box-shadow 0.2s ease, transform 0.1s ease, filter 0.2s ease
	box-shadow: variables.$shadow-button-base
	
	&:hover:not(:disabled)
		background-color: var(--noc-primary-hover, var(--noc-primary, var(--noc-blue)))
		filter: brightness(1.1)
		box-shadow: variables.$shadow-button-hover
	
	&:active:not(:disabled)
		background-color: var(--noc-primary-active, var(--noc-primary, var(--noc-blue)))
		box-shadow: variables.$shadow-button-active
		transform: translateY(1px)
	
	&:disabled
		opacity: 0.6
		background-color: var(--noc-disabled-background, #cccccc)

@include variables.responsive(mobile)
	.auth-card
		padding: variables.$spacing-large
	
</style>
